import React, { useEffect, useState } from 'react'

import { Loader, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'

import { buildSteps } from '../lib/constants'

const STEP_MS = 600

interface ReportBuildingScreenProps {
  /** Human jurisdiction labels for step 1 (e.g. ["California", "Texas"]). */
  jurisdictions: string[]
  /** True once `GET /compliance/report` has resolved. */
  ready: boolean
  /** Fires when the animation has played AND the report is ready. */
  onFinished: () => void
}

/**
 * The "Building your report…" loader. Cosmetic timed step-checklist that masks
 * the ~20s report/narrative latency — steps check off on a timer, but the final
 * step holds (spinner) until the real request resolves, so we never reveal the
 * report before it's loaded.
 */
export const ReportBuildingScreen: React.FC<ReportBuildingScreenProps> = ({
  jurisdictions,
  ready,
  onFinished,
}) => {
  const steps = buildSteps(jurisdictions)
  const [completed, setCompleted] = useState(0)

  useEffect(() => {
    const atEnd = completed >= steps.length
    // Hold the final step until the report is actually ready.
    const holdingFinal = completed === steps.length - 1 && !ready
    if (atEnd || holdingFinal) return undefined
    const t = setTimeout(() => setCompleted((c) => c + 1), STEP_MS)
    return () => clearTimeout(t)
  }, [completed, ready, steps.length])

  useEffect(() => {
    if (completed >= steps.length) onFinished()
  }, [completed, steps.length, onFinished])

  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-8 px-6 py-24">
      <Loader size={40} />
      <Text
        variant={TypographyStyles.DISPLAY_SM}
        color={TextColors.HEADER}
        textAlign="center"
      >
        Building your report…
      </Text>

      <div className="flex w-full flex-col gap-3">
        {steps.map((label, i) => {
          const isDone = i < completed
          const isActive = i === completed
          return (
            <div
              key={label}
              className={mtc(
                'flex items-center gap-3 rounded-xl border px-4 py-3 transition',
                isDone || isActive
                  ? 'border-green-25 bg-green-0'
                  : 'border-neutral-100 bg-neutral-0 opacity-50'
              )}
            >
              <span
                className={mtc(
                  'flex size-6 shrink-0 items-center justify-center rounded-full',
                  isDone ? 'bg-green-100 text-neutral-0' : 'bg-transparent'
                )}
              >
                {isDone ? (
                  <span className="text-sm leading-none">✓</span>
                ) : isActive ? (
                  <Loader size={18} inline />
                ) : (
                  <span className="size-2 rounded-full bg-neutral-200" />
                )}
              </span>
              <span className="text-neutral-900">{label}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
